import React, { Fragment, useMemo, } from 'react';
import { useToggle, } from 'react-use';
import { isEmpty, keyBy, sortBy, } from 'lodash';
import { Button, Progress, } from 'reactstrap';
import { Link } from 'react-router-dom';
import { format as formatDate, } from 'date-fns';
import classnames from 'classnames';
import qs from 'qs';

import { integerFormat, } from '../../util';
import { generateMonthlyTrendsRowGroups, monthlyTrendsRowsForExport, } from '../../shared/lib/segmentsInfo';
import { presetConsolidationAccountItems, } from '../../shared/models/consolidationAccountItem';
import { presetConsolidationJournalTypes, } from '../../shared/models/consolidationJournalType';
import { generalSegment, } from '../../shared/models/consolidationSegment';
import { accountItemCategoryNames, } from '../../shared/config';
import useCompany from '../hooks/useCompany';
import useCollectionSubscription from '../hooks/useCollectionSubscription';
import useQueryParams from '../hooks/useQueryParams';
import useMonthlySegmentsAmounts from '../hooks/useMonthlySegmentsAmounts';
import CompanyPage from '../hocs/CompanyPage';
import HelpLink from '../HelpLink';
import WithLoading from '../WithLoading';
import QuerySelector from '../QuerySelector';
import ExportButton from '../ExportButton';
import QueryBoolean from '../QueryBoolean';

const COLUMN_WIDTH = 120;

export default CompanyPage(function CompanySegmentsInfo (props) {
  const { company, period, yearMonth, filteredYearMonths, } = props;
  const subsidiaries = useCollectionSubscription(company.ref.collection('subsidiaries').orderBy('index'), [], { initialItems: null });
  const relatedCompanies = useMemo(_ => subsidiaries && [company, ...subsidiaries], [subsidiaries]);
  const queryParams = useQueryParams();
  const {
    itemKeys,
    consolidationSegmentIds,
    showsAll = '0',
  } = queryParams;
  const accountItems = useCollectionSubscription(company.ref.collection('accountItems'), [], { initialItems: null });
  const consolidationAccountItems = useCollectionSubscription(company.ref.collection('accountItems').where('subsidiaryId', '==', null), [company], { initialItems: null });
  const sortedConsolidationAccountItems = useMemo(_ => consolidationAccountItems && sortBy(consolidationAccountItems, _ => accountItemCategoryNames.indexOf(_.account_category), 'index'), [consolidationAccountItems]);
  const allConsolidationAccountItems = useMemo(_ => sortedConsolidationAccountItems && [...sortedConsolidationAccountItems, ...presetConsolidationAccountItems], [sortedConsolidationAccountItems]);
  const consolidationJournalTypes = useCollectionSubscription(company.ref.collection('consolidationJournalTypes'), [company], { initialItems: null });
  const sortedConsolidationJournalTypes = useMemo(_ => consolidationJournalTypes && sortBy(consolidationJournalTypes, 'index'), [consolidationJournalTypes]);
  const allConsolidationJournalTypes = useMemo(_ => sortedConsolidationJournalTypes && [...presetConsolidationJournalTypes, ...sortedConsolidationJournalTypes], [sortedConsolidationJournalTypes]);
  const sections = useCollectionSubscription(company.ref.collection('sections'), [company], { initialItems: null });
  const consolidationSegments = useCollectionSubscription(company.ref.collection('consolidationSegments'), [company], { initialItems: null });
  const sortedConsolidationSegments = useMemo(_ => consolidationSegments && sortBy(consolidationSegments, 'index'), [consolidationSegments]);
  const allConsolidationSegments = useMemo(_ => sortedConsolidationSegments && [...sortedConsolidationSegments, generalSegment], [sortedConsolidationSegments]);
  const { monthItems, progress, } = useMonthlySegmentsAmounts(company, relatedCompanies, allConsolidationJournalTypes, allConsolidationAccountItems, accountItems, sections, allConsolidationSegments, filteredYearMonths);

  const rowGroups = useMemo(_ => monthItems && allConsolidationSegments && generateMonthlyTrendsRowGroups(monthItems, allConsolidationSegments, showsAll === '1'), [monthItems, allConsolidationSegments, showsAll]);
  const itemKeyOptions = useMemo(_ => rowGroups?.flatMap(_ => _.rowGroups.flatMap(_ => _.itemRowGroups.map(_ => _.item))).map(({ key, item, itemType, }) => {
    return {
      label: (
        <div className={classnames({ 'font-weight-bold': itemType === 'category' })}>
          {item.name}{item.shortcut_num ? ` [${item.shortcut_num}]` : ''}
        </div>
      ),
      value: key,
      name: [item.name, item.shortcut_num].join(' '),
    };
  }), [rowGroups]);
  const consolidationSegmentOptions = useMemo(_ => (allConsolidationSegments || []).map(_ => ({ value: _.id, label: _.id === 'general' ? '調整額' : _.name })), [allConsolidationSegments]);

  const filteredRowGroups = useMemo(() => {
    if(!rowGroups) return rowGroups;
    return rowGroups.map(({ type, rowGroups }) => {
      return {
        type,
        rowGroups: rowGroups.map(({ category, itemRowGroups }) => {
          const filteredItemRowGroups = (isEmpty(itemKeys) ? itemRowGroups : itemRowGroups.filter(_ => itemKeys.includes(_.key))).map((itemRowGroup) => {
            return {
              ...itemRowGroup,
              segmentRowGroups: isEmpty(consolidationSegmentIds) ? itemRowGroup.segmentRowGroups : itemRowGroup.segmentRowGroups.filter(_ => consolidationSegmentIds.includes(_.segment.id)),
            };
          }).filter(_ => _.segmentRowGroups.length > 0);
          return { category, itemRowGroups: filteredItemRowGroups };
        }).filter(_ => _.itemRowGroups.length > 0),
      };
    }).filter(_ => _.rowGroups.length > 0);
  }, [rowGroups, itemKeys, consolidationSegmentIds]);

  return props.translate(
    <div className="company-segments-info container-fluid">
      <div className="bg-white mt-4">
        <div className="d-flex justify-content-end mb-3">
          <HelpLink text="連結セグメント情報を作成する" />
        </div>
        <div className="d-flex justify-content-center mb-3">
          <h4>連結セグメント情報</h4>
        </div>
        <WithLoading loading={!filteredRowGroups} indicator={<LoadingProgress {...progress} />}>
          <div className="mb-2 d-flex align-items-end justify-content-start gap-1 position-relative" style={{ zIndex: 3 }}>
            <QuerySelector paramName="itemKeys" width={300} isMulti options={itemKeyOptions} label="連結科目で絞込み" />
            <QuerySelector simple paramName="consolidationSegmentIds" width={200} isMulti options={consolidationSegmentOptions} label="連結セグメントで絞込み" />
            <QueryBoolean paramName="showsAll" label="金額なしの行も表示" defaultValue={'0'} />
            <ExportButton screenName="連結セグメント月次推移" rows={monthlyTrendsRowsForExport(monthItems || [], allConsolidationSegments || [], showsAll === '1')} />
          </div>
          <div className="overflow-auto" style={{ maxHeight: '100vh', }}>
            <table className="table table-bordered sticky-table table-row-hover m-0 table-sm w-auto">
              <thead className="thead-light text-center">
                <tr>
                  <th style={{ minWidth: 300, width: 300, }} className="sticky">連結科目</th>
                  <th style={{ minWidth: COLUMN_WIDTH, width: COLUMN_WIDTH, }} className="sticky">連結セグメント</th>
                  <th style={{ minWidth: COLUMN_WIDTH, width: COLUMN_WIDTH, }} className="sticky">数値項目</th>
                  <th style={{ minWidth: COLUMN_WIDTH * 1.5, width: COLUMN_WIDTH * 1.5, }} className="sticky">会社</th>
                  {
                    filteredYearMonths.map((yearMonthDate) => {
                      const columnYearMonth = formatDate(yearMonthDate, 'yyyyMM');
                      return (
                        <th key={columnYearMonth} style={{ minWidth: COLUMN_WIDTH, width: COLUMN_WIDTH, }}>
                          <Link to={`/companies/${company.id}/segmentsInfoMonth?${qs.stringify({ period, yearMonth: columnYearMonth, })}`}>
                            {formatDate(yearMonthDate, 'yyyy/MM')}
                          </Link>
                        </th>
                      );
                    })
                  }
                  <th style={{ minWidth: COLUMN_WIDTH, width: COLUMN_WIDTH, }}>合計</th>
                </tr>
              </thead>
              {
                filteredRowGroups?.map(({ type, rowGroups }) => {
                  return (
                    <tbody key={type} className="thead-light">
                      {
                        rowGroups.map(({ category, itemRowGroups, }) => {
                          return (
                            <Fragment key={category.name}>
                              {
                                itemRowGroups.map((itemRowGroup) => {
                                  return (
                                    <ItemRowGroup key={itemRowGroup.key} {...{ category, itemRowGroup, }} />
                                  );
                                })
                              }
                            </Fragment>
                          );
                        })
                      }
                    </tbody>
                  );
                })
              }
            </table>
          </div>
        </WithLoading>
      </div>
    </div>
  );
});

function ItemRowGroup (props) {
  const { category, itemRowGroup: { item: { item, itemType, }, segmentRowGroups, totalMonthColumns, totalMonthColumn, }, } = props;
  const [isOpen, toggle] = useToggle(false);
  return (
    <Fragment>
      <tr className={classnames({ 'font-weight-bold': itemType === 'category' })}>
        <th colSpan={4} className="sticky bg-white">
          <div style={{ textIndent: (category.indent() + (itemType === 'category' ? 0 : 1)) * 18 }} className={classnames({ 'font-weight-normal': itemType !== 'category' })}>
            <Button size="sm" className="small px-0 mr-1" color="link" onClick={toggle}>
              <span className={classnames('fas cursor-pointer', { 'fa-plus': !isOpen, 'fa-minus': isOpen })} />
            </Button>
            {item.name}
          </div>
        </th>
        {
          totalMonthColumns.map(({ yearMonth, amount, }) => {
            return (
              <th key={yearMonth} className="text-right">
                {amount == null ? '' : integerFormat(amount)}
              </th>
            );
          })
        }
        <th className="text-right">
          {totalMonthColumn == null ? '' : integerFormat(totalMonthColumn.amount)}
        </th>
      </tr>
      {
        isOpen && segmentRowGroups.map((segmentRowGroup) => {
          return (
            <SegmentRowGroup key={segmentRowGroup.segment.id} {...{ segmentRowGroup, }} />
          );
        })
      }
    </Fragment>
  );
}

function SegmentRowGroup (props) {
  const { segmentRowGroup: { segment, metricRows, }, } = props;
  const [isOpen, toggle] = useToggle(false);
  const displayMetricRows = isOpen ? metricRows : metricRows.filter(_ => _.rowType === 'total');
  return (
    <Fragment>
      {
        displayMetricRows.map(({ rowType, metricLabel, companyName, monthColumns, totalMonthColumn, }, i) => {
          const CellComponent = ['consolidated', 'total'].includes(rowType) ? 'th' : 'td';
          return (
            <tr key={i}>
              <CellComponent className="sticky bg-white" />
              <CellComponent className="sticky bg-white">
                {
                  i === 0 && (
                    <Fragment>
                      <Button size="sm" className="small px-0 mr-1" color="link" onClick={toggle}>
                        <span className={classnames('fas cursor-pointer', { 'fa-plus': !isOpen, 'fa-minus': isOpen })} />
                      </Button>
                      {segment.id === 'general' ? '調整額' : segment.name}
                    </Fragment>
                  )
                }
              </CellComponent>
              <CellComponent className="sticky bg-white">{metricLabel}</CellComponent>
              <CellComponent className="sticky bg-white text-truncate" style={{ maxWidth: COLUMN_WIDTH * 1.5 }}>{companyName}</CellComponent>
              {
                monthColumns.map(({ yearMonth, amount, }) => {
                  return (
                    <CellComponent key={yearMonth} className="text-right">
                      {amount == null ? '' : integerFormat(amount)}
                    </CellComponent>
                  );
                })
              }
              <CellComponent className="text-right">
                {totalMonthColumn == null ? '' : integerFormat(totalMonthColumn.amount)}
              </CellComponent>
            </tr>
          );
        })
      }
    </Fragment>
  );
}

function LoadingProgress (props) {
  const { fetchedCount, fetchTotal, computedCount, computeTotal, } = props;
  const label = fetchTotal === 0 ? '読込中...'
    : fetchedCount < fetchTotal ? `データ取得中 ${fetchedCount}/${fetchTotal}ヶ月`
    : computedCount < computeTotal ? `集計中 ${computedCount}/${computeTotal}ヶ月`
    : '表示準備中...';
  return (
    <div className="d-flex flex-column align-items-center py-5">
      <div className="mb-2">
        <span className="fas fa-spin fa-spinner mr-1" />
        {label}
      </div>
      <Progress value={fetchedCount + computedCount} max={Math.max(fetchTotal + computeTotal, 1)} style={{ width: 300 }} />
    </div>
  );
}
